/* eslint-disable react-refresh/only-export-components */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import type { Site } from '../schemas/validation';
import { ThemeProvider } from '../contexts/ThemeContext';
import { CustomColorsProvider } from '../contexts/CustomColorsContext';
import { PreviewProvider, usePreviewContext } from '../contexts/PreviewContext';
import { deriveComponentColors, isGlobalPalette } from '../utils/deriveComponentColors';
import { PoweredByBadge } from './PoweredByBadge';
import { PrivacyBanner, getConsentStatus } from './PrivacyBanner';
import { API_BASE_URL } from '../api/core';
import { applyPlatformFavicon, applySiteFavicon } from '../utils/platformFavicon';

interface WebsiteLayoutProps {
  website: Site;
  page?: {
    title: string;
    slug: string;
  };
  navigation: Array<{ title: string; slug: string; orderPosition: number }>;
  theme?: any;
  children: React.ReactNode;
  isPreviewMode?: boolean;
  previewSiteId?: string;
  /**
   * Whether to show the "Powered by CompetePages" branding badge.
   * Typically true for FREE tier users, false for paid plans.
   */
  showBranding?: boolean;
}

export { usePreviewContext };

export const WebsiteLayout: React.FC<WebsiteLayoutProps> = ({
  website,
  page: _page,
  theme,
  children,
  isPreviewMode = false,
  previewSiteId,
  showBranding = false,
}) => {
  // Note: Document title and meta tags are now handled by HeadContentComponent
  // which uses React Helmet for proper meta tag management

  // Privacy consent state - used to conditionally load third-party tracking scripts
  const [consentStatus, setConsentStatus] = useState<'accepted' | 'rejected' | null>(() =>
    getConsentStatus()
  );

  // Determine if we should load third-party scripts
  // Load if: privacy banner is disabled OR consent has been accepted
  const shouldLoadThirdPartyScripts =
    !(website as any).privacyBannerEnabled || consentStatus === 'accepted';

  // Determine if we should show the privacy banner
  // Show if: privacy banner is enabled AND consent hasn't been given yet
  const showPrivacyBanner = (website as any).privacyBannerEnabled && consentStatus === null;

  // Handle consent change from privacy banner
  const handleConsentChange = useCallback((status: 'accepted' | 'rejected') => {
    setConsentStatus(status);
    console.log('🔒 WebsiteLayout: Privacy consent changed to', status);
  }, []);

  // Customer site favicon in preview/viewer; restore Compete Pages icon when leaving.
  useEffect(() => {
    if (website.faviconUrl) {
      applySiteFavicon(website.faviconUrl);
    }
    return () => {
      applyPlatformFavicon();
    };
  }, [website.faviconUrl]);

  // Inject site-level analytics tracking scripts (GA4, GTM, Facebook Pixel)
  // Third-party scripts are gated by privacy consent when privacy banner is enabled
  useEffect(() => {
    const scriptsToCleanup: HTMLScriptElement[] = [];
    const noScriptToCleanup: HTMLElement[] = [];

    // Third-party tracking scripts - only load if consent given or privacy banner disabled
    if (shouldLoadThirdPartyScripts) {
      // Google Tag Manager
      if (website.googleTagManagerId) {
        // GTM Head script
        const gtmScript = document.createElement('script');
        gtmScript.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${website.googleTagManagerId}');`;
        gtmScript.id = 'gtm-script';
        document.head.appendChild(gtmScript);
        scriptsToCleanup.push(gtmScript);

        // GTM noscript iframe (for body)
        const gtmNoScript = document.createElement('noscript');
        gtmNoScript.id = 'gtm-noscript';
        gtmNoScript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${website.googleTagManagerId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
        document.body.insertBefore(gtmNoScript, document.body.firstChild);
        noScriptToCleanup.push(gtmNoScript);

        console.log('📊 WebsiteLayout: Injected GTM', website.googleTagManagerId);
      }

      // Google Analytics 4
      if (website.googleAnalyticsId) {
        // GA4 gtag.js script
        const gaScript = document.createElement('script');
        gaScript.async = true;
        gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${website.googleAnalyticsId}`;
        gaScript.id = 'ga4-script';
        document.head.appendChild(gaScript);
        scriptsToCleanup.push(gaScript);

        // GA4 configuration script
        const gaConfigScript = document.createElement('script');
        gaConfigScript.id = 'ga4-config';
        gaConfigScript.innerHTML = `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${website.googleAnalyticsId}');
        `;
        document.head.appendChild(gaConfigScript);
        scriptsToCleanup.push(gaConfigScript);

        console.log('📊 WebsiteLayout: Injected GA4', website.googleAnalyticsId);
      }

      // Facebook Pixel
      if (website.metaPixelId) {
        const fbScript = document.createElement('script');
        fbScript.id = 'fb-pixel-script';
        fbScript.innerHTML = `
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '${website.metaPixelId}');
          fbq('track', 'PageView');
        `;
        document.head.appendChild(fbScript);
        scriptsToCleanup.push(fbScript);

        // Facebook Pixel noscript fallback
        const fbNoScript = document.createElement('noscript');
        fbNoScript.id = 'fb-pixel-noscript';
        fbNoScript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${website.metaPixelId}&ev=PageView&noscript=1"/>`;
        document.head.appendChild(fbNoScript);
        noScriptToCleanup.push(fbNoScript);

        console.log('📊 WebsiteLayout: Injected Facebook Pixel', website.metaPixelId);
      }

      // Custom head scripts - also gated by consent as they may contain tracking
      if (website.customHeadScripts) {
        const customHeadDiv = document.createElement('div');
        customHeadDiv.id = 'custom-head-scripts';
        customHeadDiv.innerHTML = website.customHeadScripts;
        // Move script tags from div to head
        const scripts = customHeadDiv.querySelectorAll('script');
        scripts.forEach(script => {
          const newScript = document.createElement('script');
          if (script.src) newScript.src = script.src;
          if (script.innerHTML) newScript.innerHTML = script.innerHTML;
          newScript.async = script.async;
          document.head.appendChild(newScript);
          scriptsToCleanup.push(newScript);
        });
        console.log('📊 WebsiteLayout: Injected custom head scripts');
      }

      // Custom body scripts - also gated by consent as they may contain tracking
      if (website.customBodyScripts) {
        const customBodyDiv = document.createElement('div');
        customBodyDiv.id = 'custom-body-scripts';
        customBodyDiv.innerHTML = website.customBodyScripts;
        // Move script tags from div to body
        const scripts = customBodyDiv.querySelectorAll('script');
        scripts.forEach(script => {
          const newScript = document.createElement('script');
          if (script.src) newScript.src = script.src;
          if (script.innerHTML) newScript.innerHTML = script.innerHTML;
          newScript.async = script.async;
          document.body.appendChild(newScript);
          scriptsToCleanup.push(newScript);
        });
        console.log('📊 WebsiteLayout: Injected custom body scripts');
      }
    } else if ((website as any).privacyBannerEnabled) {
      console.log('🔒 WebsiteLayout: Third-party scripts blocked - waiting for privacy consent');
    }

    // Native CompetePages Analytics - always loads regardless of consent
    // (Uses sessionStorage, respects Do Not Track, doesn't set cookies)
    // Default to true if not explicitly set (for backwards compatibility)
    if (website.nativeAnalyticsEnabled !== false) {
      const siteId = website.id;
      // Use centralized API_BASE_URL from environment config
      const analyticsUrl = `${API_BASE_URL}/analytics/track`;

      const cpAnalyticsScript = document.createElement('script');
      cpAnalyticsScript.id = 'cp-native-analytics';
      cpAnalyticsScript.innerHTML = `
(function(){
  // Respect Do Not Track
  if(navigator.doNotTrack==="1")return;
  
  // Config
  var CP_SITE_ID="${siteId}";
  var CP_ANALYTICS_URL="${analyticsUrl}";
  var CP_PREVIEW=!!(new URLSearchParams(window.location.search).get("cp_preview")||new URLSearchParams(window.location.search).get("preview")||window.__CP_PREVIEW__);
  
  // Session storage keys
  var KEY_SESSION="cp_session_id";
  var KEY_SEQ="cp_pv_seq";
  var KEY_LAST="cp_last_path";
  
  // Generate UUID v4
  function uuid(){
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g,function(c){
      var r=Math.random()*16|0,v=c==="x"?r:(r&0x3|0x8);
      return v.toString(16);
    });
  }
  
  // Get or create session
  function getSession(){
    var sid=sessionStorage.getItem(KEY_SESSION);
    if(!sid){
      sid=uuid();
      sessionStorage.setItem(KEY_SESSION,sid);
      sessionStorage.setItem(KEY_SEQ,"0");
      sessionStorage.setItem(KEY_LAST,"");
    }
    return sid;
  }
  
  // Get and increment sequence
  function getSequence(){
    var seq=parseInt(sessionStorage.getItem(KEY_SEQ)||"0",10)+1;
    sessionStorage.setItem(KEY_SEQ,String(seq));
    return seq;
  }
  
  // Get last path
  function getLastPath(){
    return sessionStorage.getItem(KEY_LAST)||null;
  }
  
  // Set last path
  function setLastPath(path){
    sessionStorage.setItem(KEY_LAST,path);
  }
  
  // Send pageview
  function track(){
    var sid=getSession();
    var seq=getSequence();
    var prevPath=getLastPath();
    var currentPath=window.location.pathname;
    
    // Don't track if same path (prevents duplicate SPA triggers)
    if(seq>1&&prevPath===currentPath)return;
    
    var payload={
      siteId:CP_SITE_ID,
      eventType:"pageview",
      page:{
        path:currentPath,
        title:document.title||null
      },
      navigation:{
        sessionId:sid,
        sequence:seq,
        prevPath:seq===1?null:prevPath
      },
      referrer:document.referrer||null,
      client:{
        language:navigator.language||null,
        screenWidth:window.screen.width||null,
        screenHeight:window.screen.height||null
      },
      host:window.location.host,
      ts:new Date().toISOString(),
      preview:CP_PREVIEW
    };
    
    setLastPath(currentPath);
    
    // Send via beacon (reliable) or fetch fallback
    var data=JSON.stringify(payload);
    if(navigator.sendBeacon){
      var blob=new Blob([data],{type:'application/json'});
      navigator.sendBeacon(CP_ANALYTICS_URL,blob);
    }else{
      fetch(CP_ANALYTICS_URL,{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:data,
        keepalive:true
      }).catch(function(){});
    }
  }
  
  // Track initial page view
  track();
  
  // Hook history API for SPA navigation
  var origPushState=history.pushState;
  var origReplaceState=history.replaceState;
  
  history.pushState=function(){
    origPushState.apply(this,arguments);
    track();
  };
  
  history.replaceState=function(){
    origReplaceState.apply(this,arguments);
    track();
  };
  
  // Listen for popstate (back/forward buttons)
  window.addEventListener("popstate",track);
})();
      `;
      document.head.appendChild(cpAnalyticsScript);
      scriptsToCleanup.push(cpAnalyticsScript);

      console.log('📊 WebsiteLayout: Injected CompetePages Native Analytics for site', siteId);
    }

    // Cleanup function to remove injected scripts when component unmounts
    return () => {
      scriptsToCleanup.forEach(script => {
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
      noScriptToCleanup.forEach(el => {
        if (el.parentNode) {
          el.parentNode.removeChild(el);
        }
      });
    };
  }, [
    website.id,
    website.googleAnalyticsId,
    website.googleTagManagerId,
    website.metaPixelId,
    website.nativeAnalyticsEnabled,
    website.customHeadScripts,
    website.customBodyScripts,
    shouldLoadThirdPartyScripts,
  ]);

  // Determine the theme to use - always prefer website.theme for the theme ID
  // The theme prop may contain custom colors object, which shouldn't be used as theme ID
  const extractThemeId = (themeValue: any): string => {
    if (!themeValue) return 'standard';
    if (typeof themeValue === 'string') return themeValue;
    if (typeof themeValue === 'object') {
      // If it's an object, try to extract the ID or name
      return themeValue.id || themeValue.name || themeValue.themeName || 'standard';
    }
    return 'standard';
  };

  // Always use website.theme for theme ID, fall back to theme prop if it's a string
  const themeToUse =
    extractThemeId(website.theme) || (typeof theme === 'string' ? theme : 'standard');

  // Extract custom colors from theme object (if theme is an object with color config)
  // If the theme contains a _palette (from brand package), re-derive component colors
  // to ensure we have the latest color properties for all components
  const customColors = useMemo(() => {
    if (typeof theme !== 'object' || theme === null || Array.isArray(theme)) {
      return null;
    }

    // Check if theme has a _palette (stored by brand package editor)
    const palette = theme._palette;
    if (palette && isGlobalPalette(palette)) {
      console.log('🎨 WebsiteLayout: Re-deriving colors from brand package palette');
      // Re-derive component colors from the palette to get latest properties
      const derivedColors = deriveComponentColors(palette);
      return {
        ...derivedColors,
        // Preserve font settings from the original theme
        fontFamily: theme.fontFamily,
        headingFont: theme.headingFont,
        fontSize: theme.fontSize,
      };
    }

    // No palette found, use theme colors as-is
    return theme;
  }, [theme]);

  // Extract font settings from theme object
  const fonts =
    typeof theme === 'object' && theme !== null && !Array.isArray(theme)
      ? {
          headingFont: theme.headingFont,
          bodyFont: theme.fontFamily,
          baseSize: theme.fontSize,
        }
      : undefined;

  console.log('🎨 WebsiteLayout: Theme configuration:', {
    themeParam: theme,
    themeParamType: typeof theme,
    themeParamKeys: typeof theme === 'object' && theme !== null ? Object.keys(theme) : [],
    websiteTheme: website.theme,
    finalTheme: themeToUse,
    hasCustomColors: !!customColors,
    customColorsKeys: customColors ? Object.keys(customColors) : [],
    fonts: fonts,
  });

  // Build CSS variables for fonts
  const fontStyles: React.CSSProperties = fonts
    ? {
        ['--font-heading' as string]: fonts.headingFont
          ? `"${fonts.headingFont}", system-ui, sans-serif`
          : 'inherit',
        ['--font-body' as string]: fonts.bodyFont
          ? `"${fonts.bodyFont}", system-ui, sans-serif`
          : 'inherit',
        ['--font-size-base' as string]: fonts.baseSize || '16px',
        fontFamily: fonts.bodyFont ? `"${fonts.bodyFont}", system-ui, sans-serif` : 'inherit',
      }
    : {};

  return (
    <ThemeProvider theme={themeToUse}>
      <CustomColorsProvider customColors={customColors || {}} fonts={fonts}>
        <PreviewProvider isPreviewMode={isPreviewMode} siteId={previewSiteId}>
          <div className="website-container min-h-screen bg-white" style={fontStyles}>
            {/* Page Components (includes navbar, content, footer components) */}
            {children}
            {/* Powered by badge for FREE tier users (appears below footer) */}
            <PoweredByBadge show={showBranding} isEditorMode={isPreviewMode} />
            {/* Privacy consent banner for GDPR compliance */}
            {showPrivacyBanner && !isPreviewMode && (
              <PrivacyBanner
                customMessage={(website as any).privacyBannerMessage}
                onConsentChange={handleConsentChange}
              />
            )}
          </div>
        </PreviewProvider>
      </CustomColorsProvider>
    </ThemeProvider>
  );
};
